import { StyleSheet, Platform } from 'react-native'
import type { Theme } from '../src/theme/getTheme'
import { makeBoxShadow } from '../src/shared/helpers/shadowStyles.helper'

// Shadow color from theme so light mode gets softer edges (not harsh black)
const getTabBarShadowStyle = (theme: Theme) =>
  Platform.OS === 'web'
    ? makeBoxShadow({
        shadowColor: theme.shadow || theme.gray[700],
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      })
    : {}

/** Tab bar height; export for FAB positioning so it overlaps the bar */
export const TAB_BAR_HEIGHT = 70
/** Rounded top corners so the bar has visible edges */
const TAB_BAR_TOP_RADIUS = Platform.OS === 'web' ? 24 : 0

export const getLayoutStyles = (theme: Theme, bottomInset = 0) =>
  StyleSheet.create({
    authenticatedAppRoot: {
      flex: 1,
    },
    tabsContainer: {
      flex: 1,
      backgroundColor: theme.background.primary,
    },
    /** Behind tab scenes; keeps rounded tab bar “shoulders” from showing a mismatched color. */
    sceneContainer: {
      backgroundColor: theme.background.primary,
    },
    tabBar: {
      backgroundColor: theme.background.card,
      borderTopWidth: 0,
      borderTopLeftRadius: TAB_BAR_TOP_RADIUS,
      borderTopRightRadius: TAB_BAR_TOP_RADIUS,
      paddingBottom: Platform.OS === 'web' ? 0 : Platform.OS === 'ios' ? bottomInset - 16 : 6,
      paddingTop: Platform.OS === 'web' ? 8 : 4,
      paddingHorizontal: Platform.OS === 'android' ? 14 : Platform.OS === 'ios' ? 28 : 0,
      height: Platform.OS === 'ios' ? TAB_BAR_HEIGHT + bottomInset - 26 : TAB_BAR_HEIGHT,
      ...getTabBarShadowStyle(theme),
      elevation: 8,
    },
    tabBarWithBorder: {
      backgroundColor: theme.background.card,
      borderTopWidth: 0,
      borderTopLeftRadius: TAB_BAR_TOP_RADIUS,
      borderTopRightRadius: TAB_BAR_TOP_RADIUS,
      paddingBottom: Platform.OS === 'web' ? 0 : Platform.OS === 'ios' ? bottomInset - 16 : 6,
      paddingTop: Platform.OS === 'web' ? 8 : 4,
      paddingHorizontal: Platform.OS === 'android' ? 14 : Platform.OS === 'ios' ? 28 : 0,
      height: Platform.OS === 'ios' ? TAB_BAR_HEIGHT + bottomInset - 26 : TAB_BAR_HEIGHT,
      ...getTabBarShadowStyle(theme),
      elevation: 8,
    },
    tabBarHidden: {
      display: 'none',
    },
    friendsTabIconWrapper: {
      overflow: 'visible' as const,
      alignItems: 'center',
      justifyContent: 'center',
    },
    friendsTabBadge: {
      position: 'absolute' as const,
      top: -2,
      right: -6,
    },
    tabBarItemStyle: {
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
    },
    tabBarLabelStyle: {
      fontSize: 12,
      fontWeight: '600' as const,
      marginTop: 0,
    },
    worldCupTabBarLabelStyle: {
      fontSize: Platform.OS === 'ios' ? 10 : 12,
      fontWeight: '700' as const,
      marginTop: 0,
      ...(Platform.OS === 'ios' ? { letterSpacing: -0.35 } : {}),
    },
  })

// Backward compatibility - export default styles using light theme
import { getTheme } from '../src/theme/getTheme'
export const layoutStyles = getLayoutStyles(getTheme('light'))

// Web fullscreen CSS styles (for dynamic injection when authenticated)
// Makes app fill entire viewport for immersive fullscreen experience
export const getWebFullscreenStyles = (): string => {
  return `
    :root {
      --app-dvh: 100dvh;
    }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      height: var(--app-dvh) !important;
      min-height: var(--app-dvh) !important;
      overflow: hidden !important;
      overflow-x: hidden !important;
      overscroll-behavior: none !important;
      -webkit-overflow-scrolling: touch !important;
      box-sizing: border-box !important;
    }
    #root, [data-reactroot], [id^="expo"], body > div:first-child {
      width: 100% !important;
      max-width: 100% !important;
      height: var(--app-dvh) !important;
      min-height: var(--app-dvh) !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
      overflow-x: hidden !important;
      box-sizing: border-box !important;
    }
    * {
      box-sizing: border-box !important;
    }
  `
}

// Web immediate black background CSS (for reducing black screen time)
export const WEB_IMMEDIATE_BLACK_BG_CSS = `
  html, body {
    background-color: #000000 !important;
    margin: 0 !important;
    padding: 0 !important;
  }
`

export const appContentWrapperStyles = StyleSheet.create({
  initialSkeleton: {
    flex: 1,
    backgroundColor: '#000000',
  },
})
